import {
  Grid2 as Grid,
  Typography,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Button,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  FormHelperText,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import Draggable from "react-draggable";
import LocationCityRoundedIcon from "@mui/icons-material/LocationCityRounded";
import LaunchIcon from "@mui/icons-material/Launch";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ReactCardFlip from "react-card-flip";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch, useSelector } from "react-redux";
import Magnifier from "../customComponents/Magnifier";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import MinimizeIcon from "@mui/icons-material/Minimize";
import React, {
  useState,
  useEffect,
  useRef,
  Suspense,
  useCallback,
  useMemo,
} from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForward from "@mui/icons-material/ArrowForward";
import Footer from "../layout/Footer";

import MoreResultsList from "./MoreResultsList";
import { loadComponent } from "../../utils/loadPermitComponent";
import {
  isCurrentFormNOC,
  isNocAffidavitDoc,
  isPaisRendererDocument,
  isPlanComplianceAffidavitDoc,
  resolveInitialDocumentSize,
  isScheduleOfValuesDoc,
} from "../permitForms/permitFormHelpers";
import CreatingScheduleOfValues from "../permitForms/global/CreatingScheduleOfValues";
import axios from "axios";
import { pdfSizes } from "../../data/buildingPermit/fieldDatas";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import ESRIMap from "../propertyMap/ESRIMap";
import MapTypeSwitch from "./MapTypeSwitch";
import ShowMapSwitch from "./ShowMapSwitch";
import AdditionalPermitCitySetupForm from "./AdditionalPermitCitySetupForm";
import {
  fetchJaxFields,
  handleCreateSchedule,
  setCityFailedErrorText,
  setCityPermitButtonDisabled,
  setCreatingDocTitle,
  setDocumentImage,
  setLeftValue,
  setModalsState,
  setNoAddressError,
  setPermitLoadings,
  setPermitPanelOpen,
  setRenderMapInitials,
  setRightValue,
  setScheduleOfValues,
  setSelectedRow,
  setUsingTemplates,
} from "../../store/actions/uiActions/myPermitsActions";
import {
  handleLoadPaisTemplates,
  setEnlargeGrid,
  setPaisState,
} from "../../store/actions/permitFormActions/paisStateActions";
import { setNocState } from "../../store/actions/permitFormActions/nocStateActions";
import {
  setBuildingState,
  getFormInitials,
  setFieldsModified,
} from "../../store/actions/permitFormActions/buildingStateActions";
import JaxModal from "./realEstateComponents/JaxModal";
import {
  updatePermitPackage,
  setAutofillWorkType,
  setAutofillWorkTypeID,
  setQuickActionAddress,
} from "../../store/actions/dataActions";
import PaisResultsGrid from "../buildingComponents/PaisResultsGrid";
import SearchKeywordFields from "../permitForms/simplePaisCompanents/SearchKeywordFields";
import { getPermitTypeData, hasNOC } from "../permitForms/permitFormHelpers";
import BasePermitCard from "./realEstateComponents/BasePermitCard";
import PropertyAddressFetchModal from "../modals/PropertyAddressFetchModal";
import { isPropertyDatabaseCounty } from "../../functions/availablePermitForms";
import { copyTextSilently } from "../../utils/copyTextSilently";

// Helper to check if a county has a strategy (uses real estate preset flow)
const hasStrategy = (county) => {
  return isPropertyDatabaseCounty(county);
};

// Helper to check if a county has property database support
const hasPropertyDatabase = (county) => {
  return isPropertyDatabaseCounty(county);
};

const OpenDocuments = () => {
  const dispatch = useDispatch();
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const buildingStates = useSelector((state) => state.buildingState);
  const nocStates = useSelector((state) => state.nocState);
  const paisStates = useSelector((state) => state.paisState);

  const buttonRef = useRef(null);
  const loadPaisTemplateRef = useRef(null);

  const {
    rightValue,
    selectedRow,
    leftValue,
    selectedPermitType,
    cityFailedErrorText,
    documentImage,
    prevRowID,
    cityPermitButtonDisabled,
  } = useSelector((state) => state.myPermitsComponent);
  const formBindVersion = useSelector(
    (state) => state.myPermitsComponent.formBindVersion,
  );

  // Function to check if additional permit needs city setup
  const needsAdditionalPermitCitySetup = useCallback(() => {
    if (selectedPermitType === selectedRow?.permit_type) {
      return false; // This is the main permit, not additional
    }

    const additionalPermit =
      selectedRow?.additional_permits?.[selectedPermitType];
    if (!additionalPermit) {
      return false; // No additional permit exists
    }

    const connectToCity = additionalPermit?.connect_to_city;
    if (!connectToCity?.is_connected) {
      return false; // Not connecting to city
    }

    // Check if permit is not yet generated on Jaxepics
    const hasPermitId = connectToCity?.permit?.permit_id;
    const permitUrl = connectToCity?.permit_url;

    return !(hasPermitId && permitUrl);
  }, [
    selectedPermitType,
    selectedRow?.permit_type,
    selectedRow?.additional_permits,
  ]);

  const { createLoading } = useSelector(
    (state) => state.myPermitsComponent.permits.loadings,
  );
  const createScheduleLoading = useSelector(
    (state) => state.myPermitsComponent.citySite.createScheduleLoading,
  );
  const { remainingAmount, remainingPercentage, totalCost } = useSelector(
    (state) => state.myPermitsComponent.scheduleOfValues,
  );

  const { creatingDocTitle } = useSelector(
    (state) => state.myPermitsComponent.permits,
  );

  const { permitDrawerOpen } = useSelector(
    (state) => state.myPermitsComponent.permits.modalStates,
  );

  const { showMap, noMapAddressError } = useSelector(
    (state) => state.myPermitsComponent.duvalMap,
  );

  const { permitPanelOpen } = useSelector(
    (state) => state.myPermitsComponent.citySite,
  );

  const theme = useTheme();
  const leftGridRef = useRef(null);
  const RightGridRef = useRef(null);
  const previousParamsRef = useRef({});
  const pdfSizeDocumentRef = useRef("");
  const documentSizeControlRef = useRef(null);
  const documentSizeClicksRef = useRef({ count: 0, lastClickAt: 0 });
  const fieldInstanceClicksRef = useRef({
    control: null,
    count: 0,
    lastClickAt: 0,
  });

  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const isSinglePane = useMediaQuery("(max-width:1180px)");
  const isBelow90rem = useMediaQuery("(max-width: 1439.98px)");

  const [isDisabled, setIsDisabled] = useState(false);
  const [descriptionError, setDescriptionError] = useState("");
  const [LoadedComponent, setLoadedComponent] = useState(null);
  const [signedLoading, setSignedLoading] = useState(false);
  const [showPropertyFetchModal, setShowPropertyFetchModal] = useState(false);
  const propertyFetchCheckedRef = useRef(false);
  const [mobileView, setMobileView] = useState("form");
  const [addRowMax, setAddRowMax] = useState(false);
  const [, setSovFile] = useState(null);
  const headerRef = useRef(null);
  const rightGridHeaderRef = useRef(null);
  const bottomBarRef = useRef(null);
  const [formHeight, setFormHeight] = useState(0);
  const [singlePaneOffsets, setSinglePaneOffsets] = useState({
    headerH: 0,
    bottomH: 0,
    headerTextH: 0,
  });
  const fallbackSinglePaneOffset = 176;
  const activeSinglePaneOffset =
    singlePaneOffsets.headerH ||
    singlePaneOffsets.bottomH ||
    singlePaneOffsets.headerTextH
      ? singlePaneOffsets.headerH +
        singlePaneOffsets.bottomH +
        singlePaneOffsets.headerTextH +
        (isMd ? 125 : 130)
      : fallbackSinglePaneOffset;
  const singlePaneFormScrollHeight = isSinglePane
    ? `calc(100dvh - ${activeSinglePaneOffset}px)`
    : undefined;
  const singlePanePreviewScrollHeight = isSinglePane
    ? `calc(100dvh - ${singlePaneOffsets.headerH || 0}px)`
    : undefined;

  const currentPermitData = useMemo(
    () => getPermitTypeData(selectedRow, selectedPermitType),
    [selectedRow, selectedPermitType],
  );
  const currentDocument =
    currentPermitData?.selected_documents?.[leftValue] || null;
  const currentDocumentPayload = currentDocument?.form_id
    ? currentPermitData?.documents?.[currentDocument.form_id]
    : null;
  const currentDocumentUsesPaisRenderer = isPaisRendererDocument(
    currentDocument,
    currentDocumentPayload,
  );

  useEffect(() => {
    const handleDocumentSizeClick = (event) => {
      const control = documentSizeControlRef.current;
      if (!control || event.button !== 0) return;

      const bounds = control.getBoundingClientRect();
      const isInsideControl =
        event.clientX >= bounds.left &&
        event.clientX <= bounds.right &&
        event.clientY >= bounds.top &&
        event.clientY <= bounds.bottom;

      if (!isInsideControl) {
        documentSizeClicksRef.current = { count: 0, lastClickAt: 0 };
        return;
      }

      const now = Date.now();
      const previous = documentSizeClicksRef.current;
      const count = now - previous.lastClickAt <= 750 ? previous.count + 1 : 1;

      documentSizeClicksRef.current = { count, lastClickAt: now };
      if (count !== 4) return;

      documentSizeClicksRef.current = { count: 0, lastClickAt: 0 };
      const formId =
        currentPermitData?.selected_documents?.[leftValue]?.form_id;
      if (!formId) return;

      copyTextSilently(formId);
    };

    document.addEventListener("mousedown", handleDocumentSizeClick, true);
    return () =>
      document.removeEventListener("mousedown", handleDocumentSizeClick, true);
  }, [currentPermitData?.selected_documents, leftValue]);

  useEffect(() => {
    const handleFieldInstanceClick = (event) => {
      const formContainer = RightGridRef.current;
      let muiSelectControl = event.target?.closest?.(
        '[role="combobox"], .MuiSelect-select',
      );
      const previousSelectControl = fieldInstanceClicksRef.current.control;
      const isSelectBackdropClick =
        !muiSelectControl &&
        event.target?.closest?.(".MuiBackdrop-root") &&
        previousSelectControl?.matches?.(
          '[role="combobox"], .MuiSelect-select',
        );

      if (isSelectBackdropClick) {
        const bounds = previousSelectControl.getBoundingClientRect();
        const isAtSelectControl =
          event.clientX >= bounds.left &&
          event.clientX <= bounds.right &&
          event.clientY >= bounds.top &&
          event.clientY <= bounds.bottom;

        if (isAtSelectControl) {
          muiSelectControl = previousSelectControl;
        }
      }

      const muiSelectInput = muiSelectControl
        ?.closest(".MuiInputBase-root")
        ?.querySelector("input");
      const control =
        event.target?.closest?.("input, textarea") || muiSelectInput;
      const clickTarget = muiSelectControl || control;
      const excludedInputTypes = [
        "button",
        "color",
        "file",
        "hidden",
        "image",
        "range",
        "reset",
        "submit",
      ];

      if (
        !formContainer ||
        event.button !== 0 ||
        !control ||
        !formContainer.contains(control) ||
        (control.tagName === "INPUT" &&
          !muiSelectControl &&
          excludedInputTypes.includes(control.type?.toLowerCase()))
      ) {
        fieldInstanceClicksRef.current = {
          control: null,
          count: 0,
          lastClickAt: 0,
        };
        return;
      }

      const now = Date.now();
      const previous = fieldInstanceClicksRef.current;
      const count =
        previous.control === clickTarget && now - previous.lastClickAt <= 750
          ? previous.count + 1
          : 1;

      fieldInstanceClicksRef.current = {
        control: clickTarget,
        count,
        lastClickAt: now,
      };
      if (count !== 4) return;

      fieldInstanceClicksRef.current = {
        control: null,
        count: 0,
        lastClickAt: 0,
      };

      const fieldInstanceElement = control.closest("[data-field-instance-id]");
      const directFieldInstanceId =
        control.dataset?.fieldInstanceId ||
        fieldInstanceElement?.getAttribute("data-field-instance-id");

      if (directFieldInstanceId) {
        copyTextSilently(directFieldInstanceId);
        return;
      }

      const fieldName = control.name || control.id;
      if (!fieldName) return;

      const matchingFields = (
        buildingStates?.initials?.[0]?.fields || []
      ).filter(
        (field) =>
          field?.field_instance_id &&
          (field?.name === fieldName || field?.field_name === fieldName),
      );

      if (!matchingFields.length) return;

      const matchingControls = Array.from(
        formContainer.querySelectorAll("input, textarea"),
      ).filter((input) => (input.name || input.id) === fieldName);
      const controlIndex = matchingControls.indexOf(control);
      const matchingField = matchingFields[controlIndex] || matchingFields[0];

      copyTextSilently(matchingField.field_instance_id);
    };

    document.addEventListener("mousedown", handleFieldInstanceClick, true);
    return () =>
      document.removeEventListener("mousedown", handleFieldInstanceClick, true);
  }, [buildingStates?.initials]);

  // Guard: do not render any inner content until we have the correct permit data resolved
  const currentDocIsNOC =
    currentPermitData?.selected_documents?.[leftValue]?.is_noc === true;

  const nocDoc = currentPermitData?.selected_documents?.find(
    (doc) => doc.is_noc === true,
  );
  const nocFormId = nocDoc?.form_id;
  const nocDocumentData = nocFormId
    ? currentPermitData?.documents?.[nocFormId]
    : undefined;
  const activeSelectedDoc = currentPermitData?.selected_documents?.[leftValue];
  const activeDocLabel = activeSelectedDoc?.label?.toLowerCase?.() || "";
  const isSovDoc = activeDocLabel.includes("schedule of values");
  const isCurrentDocCreated = Boolean(
    currentPermitData?.selected_documents?.[leftValue]?.created,
  );
  const numericRemainingAmount =
    typeof remainingAmount === "number"
      ? remainingAmount
      : parseFloat(String(remainingAmount).replace(/,/g, ""));
  const numericRemainingPercentage =
    typeof remainingPercentage === "number"
      ? remainingPercentage
      : parseFloat(String(remainingPercentage));
  const sovHasUnallocatedJobCost =
    isSovDoc &&
    isCurrentDocCreated &&
    totalCost > 0 &&
    ((Number.isFinite(numericRemainingAmount) &&
      Math.abs(numericRemainingAmount) > 0.000001) ||
      (Number.isFinite(numericRemainingPercentage) &&
        Math.abs(numericRemainingPercentage) > 0.000001));

  useEffect(() => {
    if (!permitDrawerOpen || !currentPermitData?.documents) return;

    const currentDoc = currentPermitData?.selected_documents?.[leftValue];
    const formId = currentDoc?.form_id;
    const documentsDoc = formId
      ? currentPermitData?.documents?.[formId]
      : undefined;
    const currentDocLabel = currentDoc?.label?.toLowerCase?.() || "";
    const isSovDoc = currentDocLabel.includes("schedule of values");
    const usesPaisRenderer = isPaisRendererDocument(currentDoc, documentsDoc);

    // Set title if changed
    if (creatingDocTitle !== currentDoc?.label) {
      dispatch(setCreatingDocTitle(currentDoc?.label));
    }

    // Determine correct image
    let newImage = null;

    if (isSovDoc) {
      newImage = documentsDoc?.signed_url || currentDoc?.img;
    } else if (!documentsDoc?.is_created) {
      if (usesPaisRenderer && documentsDoc?.signed?.length > 0) {
        newImage = documentsDoc?.signed;
      } else {
        newImage = currentDoc?.img;
      }
    } else {
      newImage = documentsDoc?.signed;
    }

    // Normalize to array for downstream rendering
    const normalizedImage = Array.isArray(newImage)
      ? newImage
      : newImage
        ? [newImage]
        : [];

    // Only dispatch if it's different
    if (JSON.stringify(documentImage) !== JSON.stringify(normalizedImage)) {
      dispatch(setDocumentImage(normalizedImage));
    }
  }, [
    permitDrawerOpen,
    currentPermitData?.documents,
    currentPermitData?.selected_documents,
    leftValue,
    creatingDocTitle,
    documentImage,
    dispatch,
  ]);

  useEffect(() => {
    if (noMapAddressError) {
      const timeOut = setTimeout(() => {
        dispatch(setNoAddressError(false));
      }, 5000);
      return () => clearTimeout(timeOut);
    }
  }, [noMapAddressError, dispatch]);

  // Check if we should show property fetch modal
  useEffect(() => {
    if (!permitDrawerOpen || propertyFetchCheckedRef.current) return;

    const isRealEstateCounty = hasPropertyDatabase(selectedRow?.county);
    const hasNoNOC = !hasNOC(selectedRow, selectedPermitType);
    const hasNoProjectAddress = !selectedRow?.project_address;

    // Show modal if: real estate county + no NOC selected + no project address
    if (isRealEstateCounty && hasNoNOC && hasNoProjectAddress) {
      // Add small delay to ensure modal renders smoothly after OpenDocuments opens
      const timer = setTimeout(() => {
        setShowPropertyFetchModal(true);
        propertyFetchCheckedRef.current = true;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [permitDrawerOpen, selectedRow, selectedPermitType]);

  // Reset property fetch check when drawer closes
  useEffect(() => {
    if (!permitDrawerOpen) {
      propertyFetchCheckedRef.current = false;
      mapInitializedRef.current = false;
      setShouldRenderMap(false);
    }
  }, [permitDrawerOpen]);

  // Memoize map rendering condition for NOC case to prevent unnecessary recalculations
  const nocMapRenderCondition = useMemo(() => {
    return (
      permitDrawerOpen &&
      currentDocIsNOC &&
      selectedPermitType === selectedRow?.permit_type &&
      selectedRow?.county === "Duval" &&
      showMap === true
    );
  }, [
    permitDrawerOpen,
    selectedPermitType,
    selectedRow?.permit_type,
    selectedRow?.county,
    showMap,
    currentDocIsNOC,
  ]);

  const shouldHideBottomBar = useMemo(() => {
    return (
      hasStrategy(selectedRow?.county) &&
      isCurrentFormNOC(selectedRow, selectedPermitType, leftValue) &&
      !selectedRow?.noc_created
    );
  }, [selectedRow, selectedPermitType, leftValue]);

  // Delay map rendering for NOC case to prevent rerender issues on first open
  useEffect(() => {
    if (!permitDrawerOpen) {
      setShouldRenderMap(false);
      return;
    }

    if (!nocMapRenderCondition) {
      setShouldRenderMap(false);
      // Reset initialization flag only when condition changes away from NOC map
      if (mapInitializedRef.current && !needsAdditionalPermitCitySetup()) {
        mapInitializedRef.current = false;
      }
      return;
    }

    // If map hasn't been initialized yet for this session, delay rendering to let effects settle
    if (!mapInitializedRef.current) {
      const timer = setTimeout(() => {
        setShouldRenderMap(true);
        mapInitializedRef.current = true;
      }, 350); // Small delay to let initial effects complete
      return () => clearTimeout(timer);
    } else {
      // Map already initialized for this session, render immediately
      setShouldRenderMap(true);
    }
  }, [permitDrawerOpen, nocMapRenderCondition, needsAdditionalPermitCitySetup]);

  useEffect(() => {
    document.addEventListener("contextmenu", function (e) {
      e.preventDefault();
    });
  }, []);

  useEffect(() => {
    if (!loadPaisTemplateRef?.current) {
      loadPaisTemplateRef.current = true;

      const hasPais = currentPermitData?.selected_documents?.filter((item) => {
        const label = item?.label?.toLowerCase?.() || "";
        return (
          label.includes("product approval information sheet") ||
          /\bpais\b/.test(label) ||
          /\bpass\b/.test(label)
        );
      });
      if (hasPais && hasPais.length > 0) {
        dispatch(handleLoadPaisTemplates());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const renderMapRef = useRef(false);
  const mapInitializedRef = useRef(false);
  const [shouldRenderMap, setShouldRenderMap] = useState(false);

  const renderedCityFieldsRef = useRef(null);

  useEffect(() => {
    const shouldFetch =
      selectedRow?.noc_created &&
      selectedRow?.connect_to_city?.is_connected &&
      selectedRow?.connect_to_city?.permit_url &&
      selectedRow?.task_id &&
      selectedRow.task_id !== prevRowID &&
      !renderedCityFieldsRef?.current;

    if (shouldFetch) {
      renderedCityFieldsRef.current = true;
      dispatch(fetchJaxFields());
    }
  }, [
    selectedRow?.task_id,
    selectedRow?.noc_created,
    selectedRow?.connect_to_city?.is_connected,
    selectedRow?.connect_to_city?.permit_url,
    prevRowID,
    dispatch,
  ]);

  useEffect(() => {
    if (cityFailedErrorText) {
      dispatch(setCityFailedErrorText(""));
    }
  }, [cityFailedErrorText, dispatch]);

  useEffect(() => {
    if (renderMapRef.current) return;

    renderMapRef.current = true;

    const additionClean =
      !selectedRow?.noc_created &&
      !selectedRow?.realEstatePresets?.realEstateSet
        ? { selectedAddress: "" }
        : !selectedRow?.noc_created &&
            selectedRow?.realEstatePresets?.realEstateSet
          ? { selectedAddress: selectedRow?.realEstatePresets?.selectedAddress }
          : { selectedAddress: selectedRow?.project_address };

    if (selectedRow?.noc_created) {
      dispatch(
        setRenderMapInitials({
          showMap: false,
          mapSatellite: true,
          mapType: "hybrid",
          addressOptions: [],
          selectedAddressTemp: "",
          selectedAddressShort: "",
          addressMapClearing: false,
          additionClean: additionClean,
        }),
      );
    } else {
      dispatch(
        setRenderMapInitials({
          showMap: true,
          mapSatellite: true,
          mapType: "hybrid",
          addressOptions: [],
          selectedAddressTemp: "",
          selectedAddressShort: "",
          addressMapClearing: false,
          additionClean: additionClean,
        }),
      );
    }
  }, [
    dispatch,
    selectedRow?.noc_created,
    selectedRow?.realEstatePresets?.formattedAddress,
    selectedRow?.realEstateSet,
    selectedRow?.realEstatePresets?.realEstateSet,
    selectedRow?.realEstatePresets?.selectedAddress,
    selectedRow?.project_address,
    selectedRow?.task_id,
  ]);

  // Ensure switch from map to form if NOC becomes created after initial mount
  useEffect(() => {
    if (!selectedRow?.noc_created) return;

    const additionClean =
      !selectedRow?.noc_created &&
      !selectedRow?.realEstatePresets?.realEstateSet
        ? { selectedAddress: "" }
        : !selectedRow?.noc_created &&
            selectedRow?.realEstatePresets?.realEstateSet
          ? { selectedAddress: selectedRow?.realEstatePresets?.selectedAddress }
          : { selectedAddress: selectedRow?.project_address };

    dispatch(
      setRenderMapInitials({
        showMap: false,
        mapSatellite: true,
        mapType: "hybrid",
        addressOptions: [],
        selectedAddressTemp: "",
        selectedAddressShort: "",
        addressMapClearing: false,
        additionClean: additionClean,
      }),
    );
  }, [
    dispatch,
    selectedRow?.noc_created,
    selectedRow?.realEstatePresets?.formattedAddress,
    selectedRow?.realEstateSet,
    selectedRow?.realEstatePresets?.realEstateSet,
    selectedRow?.realEstatePresets?.selectedAddress,
    selectedRow?.project_address,
  ]);

  const resetCreateLoading = useCallback(() => {
    if (createLoading) {
      dispatch(setPermitLoadings({ createLoading: false }));
    }
  }, [createLoading, dispatch]);

  useEffect(() => {
    resetCreateLoading();
  }, [resetCreateLoading]);

  useEffect(() => {
    const currentDoc = currentPermitData?.selected_documents?.[leftValue];
    const county = selectedRow?.county;
    const city = selectedRow?.city;
    const formId = currentDoc?.form_id;
    const currentDocLabel = currentDoc?.label?.toLowerCase?.() || "";
    const isSovDoc = currentDocLabel.includes("schedule of values");

    if (isSovDoc) {
      setLoadedComponent(null);
      previousParamsRef.current = {
        formId,
        county,
        city,
        permitDrawerOpen,
        property_data: selectedRow?.property_data,
        property_owner: selectedRow?.property_owner,
      };
      return;
    }

    const load = async () => {
      await dispatch(setBuildingState({ initials: [] }));
      const documentPayload = formId
        ? currentPermitData?.documents?.[formId]
        : null;
      const { default: Component } = await loadComponent(
        currentDoc,
        documentPayload,
      );
      setLoadedComponent(() => Component);

      // Trigger getFormInitials after component loads, especially for NOC forms
      // This ensures forms re-initialize when modal opens or property data changes
      if (currentDocIsNOC || permitDrawerOpen) {
        await dispatch(getFormInitials());
      }
    };

    const prev = previousParamsRef.current;
    const hasChanged =
      prev.formId !== formId || prev.county !== county || prev.city !== city;

    // Check for property data changes using specific primitive values to avoid object reference issues
    const hasPropertyData = !!selectedRow?.property_data?.project_address;
    const hadPropertyData = !!prev.property_data?.project_address;
    const propertyDataChanged = hasPropertyData && !hadPropertyData;

    const hasPropertyOwner =
      !!selectedRow?.property_owner?.address_parts?.address1;
    const hadPropertyOwner = !!prev.property_owner?.address_parts?.address1;
    const propertyOwnerChanged = hasPropertyOwner && !hadPropertyOwner;

    // Also trigger if modal just opened or property data became available
    const shouldReload =
      currentDoc &&
      (hasChanged ||
        (permitDrawerOpen && !prev.permitDrawerOpen) ||
        propertyDataChanged ||
        propertyOwnerChanged);

    if (shouldReload) {
      previousParamsRef.current = {
        formId,
        county,
        city,
        permitDrawerOpen,
        property_data: selectedRow?.property_data,
        property_owner: selectedRow?.property_owner,
      };
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPermitData?.selected_documents,
    currentPermitData?.documents,
    selectedRow?.county,
    selectedRow?.city,
    selectedRow?.property_data?.project_address,
    selectedRow?.property_owner?.address_parts?.address1,
    leftValue,
    permitDrawerOpen,
    dispatch,
  ]);

  useEffect(() => {
    if (descriptionError) {
      const interv = setTimeout(() => {
        setDescriptionError(false);
      }, 6000);
      return () => clearTimeout(interv);
    }
  }, [descriptionError]);

  useEffect(() => {
    if (leftGridRef.current) {
      leftGridRef.current.scrollTo(0, 0);
    }
  }, [documentImage]);

  useEffect(() => {
    if (RightGridRef.current) {
      RightGridRef.current.scrollTo(0, 0);
    }
  }, [leftValue]);

  useEffect(() => {
    if (!isSinglePane) {
      return;
    }

    const updateOffsets = () => {
      const headerH = headerRef.current?.getBoundingClientRect().height || 0;
      const headerTextH =
        rightGridHeaderRef.current?.getBoundingClientRect().height || 0;
      const bottomH = bottomBarRef.current?.getBoundingClientRect().height || 0;

      setSinglePaneOffsets((prev) =>
        prev.headerH === headerH &&
        prev.bottomH === bottomH &&
        prev.headerTextH === headerTextH
          ? prev
          : { headerH, bottomH, headerTextH },
      );
    };

    updateOffsets();

    const observers = [];
    if (headerRef.current && typeof ResizeObserver !== "undefined") {
      const headerObserver = new ResizeObserver(updateOffsets);
      headerObserver.observe(headerRef.current);
      observers.push(headerObserver);
    }

    if (bottomBarRef.current && typeof ResizeObserver !== "undefined") {
      const bottomObserver = new ResizeObserver(updateOffsets);
      bottomObserver.observe(bottomBarRef.current);
      observers.push(bottomObserver);
    }

    if (rightGridHeaderRef.current && typeof ResizeObserver !== "undefined") {
      const rightGridHeaderObserver = new ResizeObserver(updateOffsets);
      rightGridHeaderObserver.observe(rightGridHeaderRef.current);
      observers.push(rightGridHeaderObserver);
    }

    window.addEventListener("resize", updateOffsets);

    return () => {
      observers.forEach((observer) => observer.disconnect());
      window.removeEventListener("resize", updateOffsets);
    };
  }, [isSinglePane, mobileView]);

  useEffect(() => {
    const updateHeight = () => {
      const height =
        130 +
        (rightGridHeaderRef?.current?.offsetHeight ?? 0) +
        (bottomBarRef?.current?.offsetHeight ?? 0);
      setFormHeight(`calc(100% - ${height}px)`);
    };

    const observer = new ResizeObserver(updateHeight);

    if (rightGridHeaderRef?.current)
      observer.observe(rightGridHeaderRef.current);
    if (bottomBarRef?.current) observer.observe(bottomBarRef.current);

    updateHeight();

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCity = (city) => {
    // Split the string by underscores
    const words = city.split("_");

    // Capitalize the first letter of each word
    const capitalizedWords = words.map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1),
    );

    // Join the words and add 'Creating' and 'Form'
    return `${capitalizedWords.join(" ")}`;
  };

  //! UPDATE CREATED SIGNED IMAGE URL
  const handleImageError = async (index) => {
    try {
      setSignedLoading(true);
      const creatingDoc =
        currentPermitData?.selected_documents?.[leftValue]?.form_id;

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/building/update-signed-pp-url`,
        {
          task_id: selectedRow?.task_id,
          currentSession: currentSession,
          currentForm: creatingDoc,
          selectedPermitType:
            selectedRow?.leftSidePermitType ||
            selectedRow?.selectedPermitType ||
            selectedPermitType,
          mainPermitType:
            (selectedRow?.leftSidePermitType ||
              selectedRow?.selectedPermitType ||
              selectedPermitType) === selectedRow?.permit_type,
        },
        { withCredentials: true },
      );

      setSignedLoading(false);

      if (response?.data?.success) {
        const updatedUrls = response?.data?.data?.[creatingDoc]; // Updated signed URLs

        // Ensure the response has a valid URL at the specified index
        if (updatedUrls && updatedUrls.length > index) {
          return updatedUrls[index]; // Return the URL for the specified index
        }
      } else {
        console.error("Failed to update signed URL:", response?.data?.error);
        setSignedLoading(false);
      }

      return null; // Return null if no update is available
    } catch (error) {
      setSignedLoading(false);
      console.error("Error updating signed URL:", error);
      return null; // Return null on failure
    }
  };
  // ! ################################//? ################################

  //? ################################
  const LEGACY_ASSET_BASE_URL = "https://sr360noc.com";

  const handleIMG = () => {
    const extractOrder = (url) => {
      const match = url.match(/_(\d+|[A-Z])\.jpg/);
      if (match) {
        const value = match[1];
        return isNaN(value) ? value : parseInt(value, 10);
      }
      return null; // No suffix means it's the first image
    };

    const images = Array.isArray(documentImage)
      ? documentImage
      : documentImage
        ? [documentImage]
        : [];

    const sortedImages = [...images].sort((a, b) => {
      const orderA = extractOrder(a);
      const orderB = extractOrder(b);

      // If either has no suffix, keep it first
      if (orderA === null) return -1;
      if (orderB === null) return 1;

      // Ensure sorting happens within the same type (numbers vs. letters)
      if (typeof orderA === "number" && typeof orderB === "number") {
        return orderA - orderB; // Sort numbers numerically
      }
      if (typeof orderA === "string" && typeof orderB === "string") {
        return orderA.localeCompare(orderB); // Sort letters alphabetically
      }

      return 0;
    });

    // Energy Form R402 is a frontend-managed legacy form whose stored image
    // paths are relative and only resolve against the production asset host,
    // not the dev tunnel used for REACT_APP_URL.
    const isEnergyForm =
      currentPermitData?.selected_documents?.[leftValue]?.form_id === "energy";

    return (
      <>
        {sortedImages.map((item, index) => {
          const src = item.startsWith("https")
            ? item
            : isEnergyForm
              ? `${LEGACY_ASSET_BASE_URL}${item}`
              : `${process.env.REACT_APP_URL}${item}`;

          return (
            <React.Fragment key={index}>
              <Magnifier
                src={src}
                lensSize={450}
                zoomFactor={1.5}
                handleImageError={() => handleImageError(index)}
                signedLoading={signedLoading}
              />
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const handleIconButton = () => {
    if (buildingStates?.fieldsModified) {
      dispatch(setFieldsModified(false));
    }
    dispatch(setCityPermitButtonDisabled(false));
    dispatch(setAutofillWorkTypeID(null));
    dispatch(setAutofillWorkType(false));
    dispatch(
      setQuickActionAddress({
        fullStreetAddress: "",
        houseNumber: "",
        streetName: "",
        county: "",
        city: "",
        parcelID: "",
        coNum: "",
      }),
    );

    const isMainPermit = selectedPermitType === selectedRow?.permit_type;

    if (buildingStates?.submitDisabled || buildingStates?.addingNewDesignPro) {
      return;
    }
    const creatingDoc = currentPermitData?.selected_documents?.[leftValue];
    // Reset states for PAIS-related docs
    if (
      isPaisRendererDocument(
        creatingDoc,
        creatingDoc?.form_id
          ? currentPermitData?.documents?.[creatingDoc.form_id]
          : null,
      )
    ) {
      dispatch(setPaisState({ resettingData: true, usingTemplate: false }));
    }

    // Reset NOC-specific UI state
    if (currentDocIsNOC) {
      dispatch(setNocState({ cardFlip: false }));
    }

    // Navigation logic
    if (selectedRow?.city_scraper?.is_accepted) {
      dispatch(setRightValue(4));
    } else if (isMainPermit) {
      // Main permit logic - keep existing behavior
      if (
        selectedRow?.permit_package_created ||
        (selectedRow?.noc_created &&
          currentPermitData?.selected_documents?.length === 1)
      ) {
        if (rightValue !== 2) {
          dispatch(setRightValue(2));
        }
      }
    } else {
      // Additional permit logic - check if all documents are created
      const allDocumentsCreated = currentPermitData?.selected_documents?.every(
        (doc) =>
          currentPermitData?.documents?.[doc.form_id]?.is_created === true,
      );

      if (allDocumentsCreated) {
        if (rightValue !== 2) {
          dispatch(setRightValue(2));
        }
      }
    }

    // Delay drawer close slightly if needed to avoid render collision
    setTimeout(() => {
      dispatch(setModalsState({ permitDrawerOpen: false }));
    }, 50);
  };

  const handleNavigate = useCallback(
    async (direction) => {
      // try {
      let newIndex;
      let nextForm;
      if (direction === "back") {
        newIndex = leftValue - 1;
        // setBackLoading(true);
      } else {
        // setForwardLoading(true);
        newIndex = leftValue + 1;
      }

      if (direction === "back") {
        nextForm = currentPermitData?.selected_documents[leftValue - 1];
      } else {
        nextForm = currentPermitData?.selected_documents[leftValue + 1];
      }

      dispatch(setCreatingDocTitle(nextForm?.label));
      await dispatch(setLeftValue(newIndex));
      dispatch(setNocState({ cardFlip: false }));
      dispatch(setCityPermitButtonDisabled(false));
      setLoadedComponent(null);
    },
    [leftValue, dispatch, currentPermitData?.selected_documents],
  );

  useEffect(() => {
    const currentDoc = currentPermitData?.selected_documents?.[leftValue];
    if (!currentDoc?.form_id) return;

    const documentKey = `${selectedPermitType || ""}:${currentDoc.form_id}`;

    // Initialize the size only when navigation changes the active document.
    // pdfSize changes for this same document are user choices and must not be
    // overwritten by this effect.
    if (pdfSizeDocumentRef.current === documentKey) return;
    pdfSizeDocumentRef.current = documentKey;

    dispatch(setBuildingState({ sizeChangedText: "" }));

    const runtimeDocument =
      currentPermitData?.documents?.[currentDoc?.form_id] || {};
    const initialSize = resolveInitialDocumentSize({
      selectedDocument: currentDoc,
      runtimeDocument,
      isNoc: currentDocIsNOC,
    });

    if (buildingStates?.pdfSize !== initialSize) {
      dispatch(setBuildingState({ pdfSize: initialSize }));
    }
  }, [
    buildingStates?.pdfSize,
    leftValue,
    selectedPermitType,
    dispatch,
    currentPermitData?.documents,
    currentPermitData?.selected_documents,
    currentDocIsNOC,
  ]);

  const memoizedLoadedComponent = useMemo(() => {
    // Show additional permit city setup if needed
    if (needsAdditionalPermitCitySetup()) {
      return <AdditionalPermitCitySetupForm />;
    }

    if (isSovDoc) {
      return (
        <CreatingScheduleOfValues
          addRowMax={addRowMax}
          setAddRowMax={setAddRowMax}
          setFile={setSovFile}
          savingTemplate={false}
        />
      );
    }

    if (!LoadedComponent) return null;

    return (
      <Suspense fallback={<div>Loading...</div>}>
        <LoadedComponent
          setDescriptionError={setDescriptionError}
          setIsDisabled={setIsDisabled}
        />
      </Suspense>
    );
  }, [
    LoadedComponent,
    setDescriptionError,
    needsAdditionalPermitCitySetup,
    isSovDoc,
    addRowMax,
  ]);

  const showFormPaneLoader = useMemo(() => {
    if (needsAdditionalPermitCitySetup() || isSovDoc) {
      return false;
    }

    if (currentDocIsNOC) {
      return !LoadedComponent;
    }

    return !LoadedComponent;
  }, [
    LoadedComponent,
    currentDocIsNOC,
    isSovDoc,
    needsAdditionalPermitCitySetup,
  ]);

  const [startCoords, setStartCoords] = useState({ top: 0, left: 0 });
  const [animateOut, setAnimateOut] = useState(false);

  const handleClosePanel = () => {
    dispatch(
      setScheduleOfValues({
        rows: [],
        scheduleCreated: false,
        remainingAmount: "―",
        remainingPercentage: "―",
        totalCost: 0,
        totalCostDisplay: "",
        editSchedule: false,
        scheduleOfValuesDocLoading: false,
        loadedLogo: "",
        downloadLoading: false,
        savingTemplateLoading: false,
      }),
    );
    dispatch(
      setUsingTemplates({
        isUsingTemplate: false,
        templateID: "",
        templateName: "",
      }),
    );
    setAnimateOut(true);
    setTimeout(() => {
      dispatch(setPermitPanelOpen(false));
    }, 600); // Match with animation duration
  };

  const handleOpenPanel = async () => {
    const rect = buttonRef.current.getBoundingClientRect();
    setStartCoords({
      top: rect.top + rect.height / 2,
      left: rect.left + rect.width / 2,
    });
    dispatch(setPermitPanelOpen(true));
    setAnimateOut(false);
  };

  const disableFinishButton = useMemo(
    () =>
      buildingStates?.fieldsModified ||
      !currentPermitData?.selected_documents?.[leftValue]?.created ||
      buildingStates?.createDocLoading ||
      paisStates?.loadingData ||
      paisStates?.saveTemplateLoading ||
      buildingStates?.addingNewDesignPro ||
      buildingStates?.addingNewProject ||
      buildingStates?.addingNewFeeSimple ||
      buildingStates?.addingNewBonding ||
      buildingStates?.addingNewMortgage ||
      nocStates.previewLoading ||
      nocStates.editNocLoading,
    [buildingStates, currentPermitData, leftValue, paisStates, nocStates],
  );

  const nocAffidavitDoc =
    currentPermitData?.selected_documents?.find(isNocAffidavitDoc);
  const nocAffidavitData = nocAffidavitDoc
    ? currentPermitData?.documents?.[nocAffidavitDoc.form_id]
    : undefined;
  const isCurrentDocNocAffidavit = isNocAffidavitDoc(
    currentPermitData?.selected_documents?.[leftValue],
  );

  return (
    <React.Fragment>
      {paisStates?.enlargeGrid && (
        <Draggable handle=".draggable-header">
          <Box
            sx={{
              position: "absolute",
              top: "10%",
              left: 0,
              right: 0,
              margin: isBelow90rem ? "0 3%" : "0 auto",
              zIndex: 99999,
              backgroundColor: "#fff",
              maxWidth: "90rem",
              boxShadow: "2px 4px 6px rgba(0, 0, 0, 0.25)",
            }}
          >
            <Box
              className="draggable-header"
              sx={{
                cursor: "move",
                padding: "6px 0",
                fontWeight: "bold",
                borderBottom: "1px solid #eee",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
                py: 0,
                pt: 1,
                userSelect: "none",
                backgroundColor: "#e2e2e2ff",
              }}
            >
              {/* DRAG ICON */}
              <IconButton
                size="small"
                sx={{
                  cursor: "move",
                  mr: 1,
                  color: "#888", // subtle drag color
                }}
                disableRipple
                tabIndex={-1} // Don't focus this
              >
                <DragIndicatorIcon />
              </IconButton>
              <Box sx={{ flex: 1 }} />
              {/* MINIMIZE ICON */}
              <Tooltip title="Minimize Table">
                <IconButton
                  size="small"
                  onClick={() => {
                    dispatch(setEnlargeGrid(false));
                    dispatch(setPaisState({ selectedProductRow: null }));
                  }}
                  sx={{
                    color: "#555",
                    mb: 1,
                  }}
                >
                  <MinimizeIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <PaisResultsGrid />

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <SearchKeywordFields />
            </Box>
          </Box>
        </Draggable>
      )}
      <Grid
        container
        sx={{
          display: "flex",
          overflow: isSinglePane ? "visible" : "hidden",
          width: "100%",
        }}
      >
        {isSinglePane && (
          <Box
            ref={headerRef}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 3,
              backgroundColor: "#fff",
              px: 1.5,
              py: 1,
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
            }}
          >
            <Box sx={{ width: 40 }} />
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <ToggleButtonGroup
                value={mobileView}
                exclusive
                size="small"
                onChange={(_, next) => {
                  if (next) {
                    setMobileView(next);
                  }
                }}
                sx={{
                  backgroundColor: "#f7f7fb",
                  borderRadius: 2,
                  p: 0.3,
                  "& .MuiToggleButton-root": {
                    border: "none",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2.5,
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <ToggleButton value="preview">Preview</ToggleButton>
                <ToggleButton value="form">Form</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <IconButton
              onClick={handleIconButton}
              sx={{ ml: "auto" }}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}
        {/* LEFT SIDE */}
        {(!isSinglePane || mobileView === "preview") && (
          <Grid
            ref={leftGridRef}
            size={{ xs: 12, md: isSinglePane ? 12 : 6 }}
            sx={{
              height: isSinglePane
                ? singlePanePreviewScrollHeight
                : shouldHideBottomBar
                  ? "calc(90vh)"
                  : "calc(100vh - 170px)",
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
              position: "relative",
              // overflowY:
              //   currentCreatingDoc === "noc" &&
              //   selectedPermitType === selectedRow?.permit_type &&
              //   selectedRow?.county === "Duval" &&
              //   showMap === true
              //     ? "hidden"
              //     : "auto",
            }}
          >
            <Box
              sx={{
                flexDirection: "column",
                backgroundColor: "#fff",
                position: isSinglePane ? "sticky" : "fixed",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                pt: 1,
                top: 0,
                width:
                  (currentDocIsNOC &&
                    // selectedRow?.city === "jacksonville" &&
                    selectedRow?.county === "Duval" &&
                    showMap === true) ||
                  (needsAdditionalPermitCitySetup() &&
                    selectedRow?.county === "Duval")
                    ? isSinglePane
                      ? "100%"
                      : "50%"
                    : isSinglePane
                      ? "100%"
                      : "49%",
                zIndex: isSinglePane ? 2 : 9999999,
              }}
            >
              {(currentDocIsNOC &&
                selectedPermitType === selectedRow?.permit_type &&
                selectedRow?.county === "Duval") ||
              needsAdditionalPermitCitySetup() ? (
                <Box
                  sx={{
                    width: "95%",
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                  }}
                >
                  <MapTypeSwitch
                    additionalPermitNeedSetup={needsAdditionalPermitCitySetup()}
                  />
                  {!needsAdditionalPermitCitySetup() && <ShowMapSwitch />}
                  {needsAdditionalPermitCitySetup() && (
                    <Box sx={{ position: "absolute", top: 5, right: 0 }}>
                      <BasePermitCard />
                    </Box>
                  )}
                </Box>
              ) : (
                <>
                  <FormControl
                    ref={documentSizeControlRef}
                    disabled={currentDocIsNOC}
                    size="small"
                    variant="filled"
                    sx={{
                      minWidth: 290,

                      "& .MuiFilledInput-root": {
                        border: "4px solid #004976", // Darker bottom border
                        "&:hover": {
                          border: "4px solid #004976", // Ensures it stays dark on hover
                        },
                        "&.Mui-focused": {
                          border: "4px solid #004976", // Stays dark when focused
                        },
                      },
                    }}
                  >
                    <InputLabel sx={{ fontWeight: "bold" }}>
                      DOCUMENT SIZE
                    </InputLabel>
                    <Select
                      value={buildingStates?.pdfSize}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            marginTop: "1.5px",
                            maxHeight: 200, // Set the desired max height for the dropdown menu
                            width: "auto",
                            maxWidth: "100%",
                          },
                        },
                      }}
                      onChange={(e) => {
                        dispatch(setBuildingState({ pdfSize: e.target.value }));
                        const isPaisOrPass = currentDocumentUsesPaisRenderer;
                        if (
                          currentPermitData?.documents?.[
                            currentPermitData?.selected_documents?.[leftValue]
                              ?.form_id
                          ]?.is_created === true
                        ) {
                          dispatch(
                            setBuildingState({
                              sizeChangedText: isPaisOrPass
                                ? "Click 'Save Project Details/Add or Edit Product' to apply changes"
                                : "Click 'Update Document' to apply changes",
                            }),
                          );
                        } else {
                          dispatch(
                            setBuildingState({
                              sizeChangedText: isPaisOrPass
                                ? "Click 'Save Project Details/Add or Edit Product' to apply changes"
                                : "Click 'Create Document' to apply changes",
                            }),
                          );
                        }
                      }}
                    >
                      {pdfSizes.map((size, index) =>
                        size.divider ? (
                          // Render Divider & Label
                          <React.Fragment key={`divider-${index}`}>
                            <Divider />
                            <MenuItem
                              disabled
                              sx={{ fontWeight: "bold", opacity: 1 }}
                            >
                              {size.label}
                            </MenuItem>
                          </React.Fragment>
                        ) : (
                          // Render Normal Menu Items
                          <MenuItem key={size.value} value={size.value}>
                            {size.label}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </FormControl>
                  <FormHelperText sx={{ color: "#004976", fontWeight: "bold" }}>
                    {buildingStates?.sizeChangedText}
                  </FormHelperText>
                </>
              )}
            </Box>
            <Box
              sx={{
                height:
                  selectedRow?.county === "Duval" &&
                  currentDocIsNOC &&
                  selectedPermitType === selectedRow?.permit_type
                    ? "0rem"
                    : needsAdditionalPermitCitySetup()
                      ? "0rem"
                      : { xs: "3rem", md: "5rem" },
              }}
            ></Box>
            {/* "16081 Tisons Bluff Road, Jacksonville, FL 32218" */}
            {permitDrawerOpen &&
            (shouldRenderMap ||
              (needsAdditionalPermitCitySetup() &&
                selectedRow?.county === "Duval")) ? (
              <ESRIMap />
            ) : (
              <>
                {currentDocIsNOC &&
                selectedPermitType === selectedRow?.permit_type &&
                nocDocumentData?.more ? (
                  <Box>
                    <IconButton
                      sx={{
                        display: "block",
                        position: "absolute",
                        left: 20,
                        top: 20,
                        zIndex: 2,
                        backgroundColor: "#fff",
                      }}
                      onClick={() =>
                        dispatch(
                          setNocState({ cardFlip: !nocStates?.cardFlip }),
                        )
                      }
                    >
                      {nocStates.cardFlip === true ? (
                        <CloseIcon />
                      ) : (
                        <FormatListBulletedIcon />
                      )}
                    </IconButton>
                    <ReactCardFlip
                      isFlipped={nocStates.cardFlip}
                      flipDirection="horizontal"
                      flipSpeedBackToFront={1}
                      flipSpeedFrontToBack={1}
                    >
                      <Box>{handleIMG()}</Box>
                      <Box sx={{ height: "100vh" }}>
                        <MoreResultsList
                          addressRow={nocDocumentData?.more}
                          handleRowClick={(row) => {
                            dispatch(setNocState({ moreResultRow: row }));
                          }}
                        />
                      </Box>
                    </ReactCardFlip>
                  </Box>
                ) : (
                  <Box>{handleIMG()}</Box>
                )}
              </>
            )}
          </Grid>
        )}

        {/* RIGHT SIDE */}
        {(!isSinglePane || mobileView === "form") && (
          <Grid
            size={{ xs: 12, md: isSinglePane ? 12 : 6 }}
            sx={{
              position: "relative",
              mb: 1,
              mt: { xs: 0, md: 2 },
              height: isSinglePane ? "auto" : "100vh",
              borderLeft: isSinglePane ? "none" : "4px solid #f3f3f3",
            }}
          >
            {/* HEADER */}
            {!isSinglePane && (
              <IconButton
                disabled={
                  buildingStates?.createDocLoading ||
                  paisStates?.loadingData ||
                  paisStates?.saveTemplateLoading ||
                  nocStates?.editNocLoading
                }
                onClick={handleIconButton}
                sx={{ position: "absolute", right: 10, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            )}
            <Box
              ref={rightGridHeaderRef}
              sx={{
                backgroundColor: "#fff",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                pt: { xs: 2, md: 0 },
              }}
            >
              {needsAdditionalPermitCitySetup() ? (
                <>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color="primary"
                    align="center"
                    sx={{ mb: 1 }}
                  >
                    Attaching Permit Type
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mb: 1 }}
                  >
                    Setting up additional{" "}
                    {selectedPermitType
                      ?.replace("_", " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}{" "}
                    permit for city connection
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    color="primary"
                    mx={1}
                    align="center"
                    variant={isSm ? "h6" : "h5"}
                  >
                    {selectedRow?.county === "Duval" &&
                    isCurrentFormNOC(
                      selectedRow,
                      selectedPermitType,
                      leftValue,
                    ) &&
                    !selectedRow?.noc_created &&
                    selectedPermitType === selectedRow?.permit_type
                      ? "Address of the Construction Site"
                      : currentPermitData?.selected_documents?.[leftValue]
                            ?.created
                        ? `${creatingDocTitle} is Created`
                        : `Creating ${creatingDocTitle}`}
                  </Typography>
                  <Typography
                    color="primary"
                    variant="h6"
                    sx={{ fontSize: "14px !important" }}
                  >
                    {leftValue + 1} /{" "}
                    {currentPermitData?.selected_documents?.length}
                  </Typography>
                </>
              )}

              <Typography
                variant="h6"
                sx={{
                  mb: 0.5,
                  mt: 0,
                  fontSize: "14px !important",
                }}
                textAlign="center"
              >
                {selectedRow?.city && selectedRow?.city !== "county_specific"
                  ? `${formatCity(selectedRow?.city)} - ${
                      selectedRow?.county
                    } County`
                  : `${selectedRow?.county} County`}
              </Typography>
              {selectedPermitType === selectedRow?.permit_type &&
                selectedRow?.connect_to_city?.is_connected &&
                selectedRow?.connect_to_city?.permit_url &&
                selectedRow?.noc_created && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                        mb: 1,
                      }}
                    >
                      {/* Button to toggle expanding panel */}
                      <Button
                        ref={buttonRef}
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={handleOpenPanel}
                        endIcon={
                          <LocationCityRoundedIcon sx={{ color: "#fff" }} />
                        }
                        disabled={cityPermitButtonDisabled}
                      >
                        City Permit Panel
                      </Button>

                      {/* Icon to open JaxEPICS in new tab */}
                      <IconButton
                        size="small"
                        color="primary"
                        component="a"
                        href={selectedRow.connect_to_city.permit_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View in JaxEPICS"
                      >
                        <LaunchIcon />
                      </IconButton>
                    </Box>

                    <Typography
                      color="error"
                      variant="body2"
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        minHeight: "1rem",
                      }}
                    >
                      {cityPermitButtonDisabled
                        ? "Please create or update the necessary changes to enable access to the City Permit Panel"
                        : ""}
                    </Typography>

                    {/* The animated panel for permit field editing */}
                    {permitPanelOpen && (
                      <JaxModal
                        handleClosePanel={handleClosePanel}
                        startCoords={startCoords}
                        animateOut={animateOut}
                      />
                    )}
                  </>
                )}
              {currentDocIsNOC &&
              selectedPermitType === selectedRow?.permit_type &&
              !selectedRow?.noc_created &&
              selectedRow?.city === "jacksonville" &&
              selectedRow?.connect_to_city?.is_connected ? (
                <Box
                  sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}
                >
                  <CheckCircleIcon
                    sx={{ color: "green", fontSize: "1.2rem" }}
                  />
                  <Typography
                    align="center"
                    variant="body2"
                    fontWeight="bold"
                    color="success.dark"
                  >
                    Generating Permit Number
                  </Typography>
                </Box>
              ) : selectedPermitType === selectedRow?.permit_type &&
                !selectedRow?.noc_created &&
                selectedRow?.city === "jacksonville" &&
                !selectedRow?.connect_to_city?.is_connected ? (
                <Box
                  sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}
                >
                  <CancelIcon sx={{ color: "red", fontSize: "1.2rem" }} />
                  <Typography
                    align="center"
                    variant="body2"
                    fontWeight="bold"
                    color="error.dark"
                  >
                    Not Generating Permit Number
                  </Typography>
                </Box>
              ) : (
                <></>
              )}
              {noMapAddressError && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <CancelIcon sx={{ color: "red", fontSize: "1.2rem" }} />
                  <Typography variant="body1" color="error" fontWeight="bold">
                    Could not locate the address on the map
                  </Typography>
                </Box>
              )}
              {currentDocumentUsesPaisRenderer && (
                <Typography align="center" fontWeight="bold" variant="body2">
                  Product Approval form is saved when you load a template, add a
                  product or edit/remove a product
                </Typography>
              )}
            </Box>
            {/* TEXTFIELD/CARD */}
            <Box
              ref={RightGridRef}
              sx={{
                height: isSinglePane ? singlePaneFormScrollHeight : formHeight,
                overflowY: isSinglePane ? "auto" : "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                px: 0.3,
                mt: 0,
                pb: { xs: 2, md: 1.5 },
                borderTop: "4px solid #f3f3f3",
                borderBottom: needsAdditionalPermitCitySetup()
                  ? undefined
                  : shouldHideBottomBar
                    ? "none"
                    : "4px solid #f3f3f3",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Re-mount form when formBindVersion changes to rebind values */}
              <div key={formBindVersion} style={{ flex: 1 }}>
                {showFormPaneLoader ? (
                  <Box
                    sx={{
                      minHeight: 240,
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  memoizedLoadedComponent
                )}
              </div>
            </Box>
            {/* {creatingDoc ? ( */}
            {(!isSinglePane || mobileView === "form") && (
              <Toolbar
                ref={bottomBarRef}
                sx={{
                  display: shouldHideBottomBar ? "none" : "flex",
                  px: 0,
                  py: 0.5,
                  position: isSinglePane ? "sticky" : "fixed",
                  bottom: isSinglePane ? 0 : 88.5,
                  left: 0,
                  right: 0,
                  opacity: 1,
                  backgroundColor: "#fff",
                  borderTop: isSinglePane ? "1px solid #e5e7eb" : "none",
                  pb: {
                    xs: "calc(6px + env(safe-area-inset-bottom))",
                    md: 0.5,
                  },
                }}
              >
                <Grid container sx={{ width: "100%" }}>
                  <Grid size={{ xs: 12 }}>
                    {descriptionError && (
                      <Typography
                        sx={{ backgroundColor: "#fff" }}
                        mt={1}
                        ml={1}
                        pt={2}
                        color="error"
                        align="center"
                      >
                        {descriptionError}
                      </Typography>
                    )}

                    {paisStates?.paisSearching &&
                      currentDocumentUsesPaisRenderer && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            ml: "24px",
                            alignItems: "center",
                            backgroundColor: "#fff",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <LoadingButton
                            onClick={(e) => {
                              e.stopPropagation();
                              if (Boolean(paisStates?.editData)) {
                                dispatch(
                                  setPaisState({ startDeleteProduct: true }),
                                );
                              } else {
                                dispatch(setPaisState({ resettingData: true }));
                              }
                            }}
                            color="error"
                            loading={paisStates?.loadingData}
                            disabled={
                              !paisStates?.category || !paisStates?.subCategory
                            }
                            variant="outlined"
                          >
                            {Boolean(paisStates?.editData)
                              ? "Delete Product"
                              : "New Search"}
                          </LoadingButton>
                          <LoadingButton
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch(setPaisState({ isCreatingPais: true }));
                            }}
                            color="success"
                            loading={paisStates?.loadingData}
                            disabled={
                              !paisStates?.category || !paisStates?.subCategory
                            }
                            variant="contained"
                          >
                            {Boolean(paisStates?.editData)
                              ? "Edit Product"
                              : "Add Product"}
                          </LoadingButton>
                          {Boolean(paisStates?.editData) && (
                            <LoadingButton
                              onClick={(e) => {
                                e.stopPropagation();
                                dispatch(setPaisState({ resettingData: true }));
                              }}
                              color="primary"
                              loading={paisStates?.loadingData}
                              disabled={
                                !paisStates?.category ||
                                !paisStates?.subCategory
                              }
                              variant="outlined"
                            >
                              Cancel Edit
                            </LoadingButton>
                          )}
                        </Box>
                      )}
                    {needsAdditionalPermitCitySetup() ? (
                      <></>
                    ) : (
                      <>
                        <Box
                          sx={{
                            borderTop:
                              paisStates?.paisSearching &&
                              currentDocumentUsesPaisRenderer
                                ? "4px solid #f3f3f3"
                                : undefined,
                            display: "flex",
                            justifyContent: "center",
                            flexWrap: "wrap",
                            ml: isSinglePane ? 0 : "24px",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {(isCurrentDocNocAffidavit &&
                            selectedRow?.noc_notarized === true) ||
                          (!currentDocIsNOC &&
                            !isCurrentDocNocAffidavit &&
                            selectedRow?.noc_notarized === true &&
                            nocAffidavitData?.is_created) ||
                          currentPermitData?.processing_permit?.notarization
                            ?.started ? (
                            <>
                              <LoadingButton
                                onClick={() => {}}
                                disabled
                                sx={{ display: "none" }}
                                variant="contained"
                                startIcon={<ArrowBackIcon />}
                              >
                                Back
                              </LoadingButton>
                            </>
                          ) : (
                            <LoadingButton
                              onClick={() => handleNavigate("back")}
                              disabled={
                                leftValue === 0 ||
                                buildingStates?.fieldsModified ||
                                buildingStates?.createDocLoading ||
                                paisStates?.loadingData ||
                                paisStates?.saveTemplateLoading ||
                                buildingStates?.addingNewDesignPro ||
                                buildingStates?.addingNewProject ||
                                buildingStates?.addingNewFeeSimple ||
                                buildingStates?.addingNewBonding ||
                                buildingStates?.addingNewMortgage
                              }
                              sx={{
                                display:
                                  leftValue === 0 ? "none" : "inline-flex",
                              }}
                              variant="contained"
                              startIcon={<ArrowBackIcon />}
                              size={isMd ? "small" : "medium"}
                            >
                              Back
                            </LoadingButton>
                          )}

                          <Button
                            disabled={
                              buildingStates?.createDocLoading ||
                              buildingStates?.permissionLoading ||
                              paisStates?.loadingData ||
                              nocStates.previewLoading ||
                              nocStates.editNocLoading
                            }
                            sx={{
                              minWidth: "10rem",
                            }}
                            color="primary"
                            variant="outlined"
                            onClick={handleIconButton}
                            size={isMd ? "small" : "medium"}
                          >
                            Main Menu
                          </Button>

                          <LoadingButton
                            disabled={
                              buildingStates?.submitDisabled ||
                              buildingStates?.addingNewDesignPro ||
                              buildingStates?.addingNewProject ||
                              buildingStates?.addingNewFeeSimple ||
                              buildingStates?.addingNewBonding ||
                              buildingStates?.addingNewMortgage ||
                              nocStates.previewLoading ||
                              paisStates?.paisSelectedIDs?.length > 0 ||
                              (currentSession === "privateProviderSession" &&
                                isPlanComplianceAffidavitDoc(
                                  currentPermitData?.selected_documents?.[
                                    leftValue
                                  ],
                                ) &&
                                currentPermitData?.documents?.[
                                  currentPermitData?.selected_documents?.[
                                    leftValue
                                  ]?.form_id
                                ]?.is_created) ||
                              isDisabled ||
                              sovHasUnallocatedJobCost
                            }
                            // endIcon={up1177 ? <SendIcon color="#fff" /> : undefined}
                            loading={
                              buildingStates?.createDocLoading ||
                              buildingStates?.permissionLoading ||
                              nocStates.createNocLoading ||
                              nocStates.editNocLoading ||
                              buildingStates?.isCreatingForm ||
                              createScheduleLoading
                            }
                            color={
                              currentPermitData?.documents?.[
                                currentPermitData?.selected_documents?.[
                                  leftValue
                                ]?.form_id
                              ]?.is_created
                                ? "warning"
                                : "success"
                            }
                            onClick={async () => {
                              if (
                                currentDocIsNOC &&
                                !selectedRow?.noc_created
                              ) {
                                dispatch(
                                  setNocState({
                                    createNocLoading: true,
                                    isCreatingNoc: true,
                                    cardFlip: false,
                                  }),
                                );
                              } else if (
                                currentDocIsNOC &&
                                selectedRow?.noc_created
                              ) {
                                dispatch(
                                  setNocState({
                                    editNocLoading: true,
                                    isEditNoc: true,
                                  }),
                                );
                              } else if (
                                isScheduleOfValuesDoc(
                                  currentPermitData?.selected_documents?.[
                                    leftValue
                                  ],
                                )
                              ) {
                                const resp = await dispatch(
                                  handleCreateSchedule(
                                    currentPermitData?.documents?.sov
                                      ?.loadedLogo || null,
                                  ),
                                );

                                if (resp?.success && resp?.data) {
                                  await dispatch(
                                    updatePermitPackage(resp.data),
                                  );
                                  await dispatch(setSelectedRow(resp.data));
                                }
                              } else {
                                dispatch(
                                  setBuildingState({ isCreatingForm: true }),
                                );
                              }
                            }}
                            endIcon={
                              currentPermitData?.documents?.[
                                currentPermitData?.selected_documents?.[
                                  leftValue
                                ]?.form_id
                              ]?.is_created ? (
                                <EditIcon />
                              ) : undefined
                            }
                            variant="contained"
                            size={isMd ? "small" : "medium"}
                          >
                            {!currentPermitData?.selected_documents?.[leftValue]
                              ?.created
                              ? "Create Document"
                              : "Update Document"}
                          </LoadingButton>
                          {/* )} */}
                          {currentPermitData?.selected_documents?.length ===
                            1 && (
                            <Button
                              sx={{ width: "10rem" }}
                              variant="contained"
                              color="primary"
                              disabled={disableFinishButton}
                              onClick={() => {
                                dispatch(
                                  setModalsState({
                                    permitDrawerOpen: false,
                                  }),
                                );
                                dispatch(setRightValue(2));
                              }}
                              size={isMd ? "small" : "medium"}
                            >
                              Finish
                            </Button>
                          )}
                          <LoadingButton
                            sx={{
                              display:
                                currentPermitData?.selected_documents.length ===
                                1
                                  ? "none"
                                  : "inline-flex",
                              minWidth: "100px",
                            }}
                            color={
                              leftValue ===
                                currentPermitData?.selected_documents.length -
                                  1 ||
                              ((selectedRow?.noc_notarized === true ||
                                selectedRow?.processing_permit?.notarization
                                  ?.started) &&
                                isCurrentFormNOC(
                                  selectedRow,
                                  selectedPermitType,
                                  leftValue,
                                ))
                                ? "success"
                                : "primary"
                            }
                            onClick={() => {
                              if (
                                leftValue ===
                                  currentPermitData?.selected_documents.length -
                                    1 ||
                                ((selectedRow?.noc_notarized === true ||
                                  selectedRow?.processing_permit?.notarization
                                    ?.started) &&
                                  isCurrentFormNOC(
                                    selectedRow,
                                    selectedPermitType,
                                    leftValue,
                                  ))
                              ) {
                                handleIconButton();
                              } else {
                                handleNavigate("forward");
                              }
                            }}
                            //^ FOR DEV //////////////////////////////////////////////////
                            disabled={disableFinishButton}
                            variant="contained"
                            endIcon={
                              leftValue ===
                              currentPermitData?.selected_documents.length -
                                1 ? undefined : (
                                <ArrowForward />
                              )
                            }
                            size={isMd ? "small" : "medium"}
                          >
                            {leftValue ===
                              currentPermitData?.selected_documents.length -
                                1 ||
                            ((selectedRow?.noc_notarized === true ||
                              selectedRow?.processing_permit?.notarization
                                ?.started) &&
                              isCurrentFormNOC(
                                selectedRow,
                                selectedPermitType,
                                leftValue,
                              ))
                              ? "Finish"
                              : "Next"}
                          </LoadingButton>
                          {/* )} */}
                        </Box>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Toolbar>
            )}
          </Grid>
        )}
      </Grid>

      {(!isSinglePane || mobileView === "form") && (
        <Box
          component="footer"
          sx={{
            position: "fixed",
            bottom: 0,
            backgroundColor: "#1c2536",
            left: 0,
            width: "100%",
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        >
          <Footer height="60px" />
        </Box>
      )}

      {/* Property Address Fetch Modal - shown when no NOC and no project address */}
      <PropertyAddressFetchModal
        open={showPropertyFetchModal}
        onClose={() => setShowPropertyFetchModal(false)}
        onSuccess={(data) => {
          // Property data has been saved, modal will close automatically
        }}
      />
    </React.Fragment>
  );
};

export default React.memo(OpenDocuments);
